import {
    CircularProgress,
    FormControl,
    Grid,
    IconButton,
    InputLabel,
    MenuItem,
    OutlinedInput,
    Select,
    SelectChangeEvent,
    Stack,
    Typography,
    useTheme,
} from '@mui/material'
import React from 'react'
import { useAppDispatch } from '../../hooks/reduxHooks'
import { displayMessage } from '../../services/redux/snackbar/snackbar-actions'
import { ClientDto, ClientType } from '../../services/api/sharedTypes/Client'
import { convertStringToClientType } from '../../utils/client-utils'
import CustomTextField from '../common/CustomTextField'
import { ClientManager } from '../../services/api/ClientManager'
import CustomContainer from '../common/CustomContainer'
import ContactPersonsList from './ContactPersonsList'
import NotificationsList from './NotificationsList'
import SearchIcon from '@mui/icons-material/Search';

interface ClientBasicInformationProps {
    client: ClientDto
    onChange: (client: ClientDto) => void
    resetKey?: number // used to recalculate name when saveAndAdd is clicked
}

const ClientBasicInformation: React.FC<ClientBasicInformationProps> = ({ client, onChange, resetKey }: ClientBasicInformationProps) => {
    const theme = useTheme()
    const dispatch = useAppDispatch()
    const [firstName, setFirstName] = React.useState<string>(client.name.split(' ')[0])
    const [lastName, setLastName] = React.useState<string>(client.name.split(' ')[1])
    const [loadingAresData, setLoadingAresData] = React.useState<boolean>(false)

    React.useEffect(() => {
        setFirstName(client.name.split(' ')[0])
        setLastName(client.name.split(' ')[1])
    }, [resetKey])

    const handleSelectChange = (event: SelectChangeEvent<ClientType>) => {
        const {
            target: { value },
        } = event

        const type = convertStringToClientType(+value)
        onChange({
            ...client,
            clientType: type,
            companyRegistrationNumber: type === ClientType.Citizen ? '' : client.companyRegistrationNumber,
            valueAddedTaxIdentificationNumber: type === ClientType.Citizen ? '' : client.valueAddedTaxIdentificationNumber,
        })
    }

    const handleNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, nameIndex: number) => {
        const {
            target: { value },
        } = event

        if (nameIndex === 0) {
            setFirstName(value)
            onChange({ ...client, name: `${value} ${lastName ?? ''}` })
        }
        if (nameIndex === 1) {
            setLastName(value)
            onChange({ ...client, name: `${firstName ?? ''} ${value}` })
        }
    }

    const findCompanyRegistrationNumber = async () => {
        if (!client.companyRegistrationNumber) return dispatch(displayMessage({ message: 'Zadejte IČO', type: 'info' }))
        setLoadingAresData(true)
        const clientManager = ClientManager.getManager()
        try {
            const clientsRes = await clientManager?.getClientAresAsync(Number(client.companyRegistrationNumber))
            if (clientsRes) {
                onChange({
                    ...client,
                    name: clientsRes.name,
                    address: clientsRes.address,
                    valueAddedTaxIdentificationNumber: clientsRes.valueAddedTaxIdentificationNumber ?? '',
                })
            }
        } catch (error) {
            setLoadingAresData(false)
            return dispatch(displayMessage({ message: error, type: 'error' }))
        }
        setLoadingAresData(false)
    }

    return (
        <CustomContainer bgColor={theme.palette.background.default}>
            <Grid container rowSpacing={3} columnSpacing={4}>
                <Grid item xs={12} md={6}>
                    <Typography variant='h6' color='primary' mb={3}>
                        Základní údaje
                    </Typography>
                    <Grid container rowSpacing={3} columnSpacing={3}>
                        <Grid item xs={6}>
                            <FormControl size='small' fullWidth>
                                <InputLabel>Typ klienta</InputLabel>
                                <Select
                                    value={client?.clientType === ClientType.NotSet ? '' : client?.clientType}
                                    onChange={handleSelectChange}
                                    input={
                                        <OutlinedInput
                                            label='Typ klienta'
                                            style={{ background: theme.palette.customBackground.default, maxHeight: 36 }}
                                        />
                                    }
                                >
                                    <MenuItem value={ClientType.Citizen}>Občan</MenuItem>
                                    <MenuItem value={ClientType.LegalEntity}>Právnická osoba</MenuItem>
                                    <MenuItem value={ClientType.Individual}>Fyzická osoba</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {client?.clientType === ClientType.Citizen && (
                            <>
                                <Grid item xs={6} />
                                <Grid item xs={6}>
                                    <CustomTextField
                                        fullWidth
                                        variant='outlined'
                                        placeholder='Jméno'
                                        label='Jméno'
                                        size='small'
                                        value={firstName}
                                        onChange={(e) => handleNameChange(e, 0)}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <CustomTextField
                                        fullWidth
                                        variant='outlined'
                                        placeholder='Příjmení'
                                        label='Příjmení'
                                        size='small'
                                        value={lastName}
                                        onChange={(e) => handleNameChange(e, 1)}
                                    />
                                </Grid>
                            </>
                        )}
                        {(client?.clientType === ClientType.LegalEntity || client?.clientType === ClientType.Individual) && (
                            <>
                                <Grid item xs={6}>
                                    <Stack direction='row' spacing={2}>
                                        <CustomTextField
                                            fullWidth
                                            variant='outlined'
                                            placeholder='IČ'
                                            label='IČ'
                                            size='small'
                                            value={client?.companyRegistrationNumber}
                                            onChange={(e) => onChange({ ...client, companyRegistrationNumber: e.target.value })}
                                        />
                                        <IconButton
                                            disabled={loadingAresData}
                                            onClick={() => findCompanyRegistrationNumber()}
                                            color='primary'
                                            style={{
                                                border: '1px solid',
                                                borderRadius: '10%',
                                                maxWidth: '36px',
                                                maxHeight: '36px',
                                                minWidth: '36px',
                                                minHeight: '36px',
                                                width: '36px',
                                                height: '36px',
                                            }}
                                        >
                                            {loadingAresData ? <CircularProgress size={20} /> : <SearchIcon />}
                                        </IconButton>
                                    </Stack>
                                </Grid>
                                <Grid item xs={6}>
                                    <CustomTextField
                                        fullWidth
                                        variant='outlined'
                                        placeholder={client.clientType === ClientType.LegalEntity ? 'Název' : 'Jméno a příjmení'}
                                        label={client.clientType === ClientType.LegalEntity ? 'Název' : 'Jméno a příjmení'}
                                        size='small'
                                        value={client?.name}
                                        onChange={(e) => onChange({ ...client, name: e.target.value })}
                                    />
                                </Grid>
                                <Grid item xs={6}>
                                    <CustomTextField
                                        fullWidth
                                        variant='outlined'
                                        placeholder='DIČ'
                                        label='DIČ'
                                        size='small'
                                        value={client?.valueAddedTaxIdentificationNumber}
                                        onChange={(e) =>
                                            onChange({ ...client, valueAddedTaxIdentificationNumber: e.target.value })
                                        }
                                    />
                                </Grid>
                            </>
                        )}
                        <Grid item xs={6}>
                            <CustomTextField
                                fullWidth
                                variant='outlined'
                                size='small'
                                placeholder='Ulice a č.p./č.o.'
                                label='Ulice a č.p./č.o.'
                                value={client?.address.street}
                                onChange={(e) => onChange({ ...client, address: { ...client.address, street: e.target.value } })}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <CustomTextField
                                fullWidth
                                variant='outlined'
                                size='small'
                                placeholder='Město'
                                label='Město'
                                value={client?.address.city}
                                onChange={(e) => onChange({ ...client, address: { ...client.address, city: e.target.value } })}
                            />
                        </Grid>
                        <Grid item xs={2}>
                            <CustomTextField
                                variant='outlined'
                                size='small'
                                placeholder='PSČ'
                                label='PSČ'
                                value={client?.address.zip}
                                onChange={(e) => onChange({ ...client, address: { ...client.address, zip: e.target.value } })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <CustomTextField
                                fullWidth
                                variant='outlined'
                                placeholder='Poznámka'
                                label='Poznámka'
                                size='small'
                                multiline
                                rows={3}
                                value={client?.note}
                                onChange={(e) => onChange({ ...client, note: e.target.value })}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Stack spacing={3}>
                        <ContactPersonsList client={client} onChange={onChange} />
                        <NotificationsList client={client} onChange={onChange} />
                    </Stack>
                </Grid>
            </Grid>
        </CustomContainer>
    )
}

export default ClientBasicInformation
